import { Document, Font, Page, Text, View, StyleSheet as ReactPdfStyleSheet } from "@react-pdf/renderer";
import _ from "lodash";
import { useForm } from "react-hook-form";

import { ddqDescriptionText, formSectionsPerPage } from "./common";
import PDFDocument from "./PDFDocument";

import { useDDQDocument } from "~/endpoints/trustFramework";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";

// Register Roboto font for React-PDF, fixes foreign alphabet issues
Font.register({
  family: "Roboto",
  fonts: [
    { src: "https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Me5Q.ttf", fontStyle: "normal", fontWeight: "normal" },
    {
      src: "https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBBc4.ttf",
      fontStyle: "normal",
      fontWeight: "bold",
    },
    {
      src: "https://fonts.gstatic.com/s/roboto/v20/KFOkCnqEu92Fr1Mu51xIIzc.ttf",
      fontStyle: "italic",
      fontWeight: "normal",
    },
  ],
});

type Props = {
  title: JSX.Element;
  ownerVASPDID?: string;
};

export const ReactToPdfStyles = ReactPdfStyleSheet.create({
  page: {
    flexDirection: "column",
    fontSize: "12px",
    padding: 30,
    fontFamily: "Roboto",
  },
  header: {
    marginBottom: 15,
  },
  headerTitle: {
    fontSize: "20px",
    marginBottom: 15,
  },
  section: {
    flexGrow: 1,
    paddingTop: 20,
  },
  sectionTitle: {
    paddingTop: 10,
    paddingBottom: 5,
    fontSize: "18px",
  },
  question: {
    paddingTop: 15,
  },
  answer: {
    paddingTop: 10,
    paddingLeft: 10,
  },
});

export default function DDQPDFDocument({ title, ownerVASPDID }: Props) {
  const requestingVASPDID = useCurrentViewerVaspDID();
  const { data: ddqValues, isLoading } = useDDQDocument(
    ownerVASPDID,
    ownerVASPDID === requestingVASPDID ? undefined : requestingVASPDID,
  );

  const form = useForm({
    shouldUnregister: false,
    defaultValues: ddqValues?.data || {},
  });

  if (isLoading) {
    return null;
  }

  const pdfDocument = (
    <Document>
      <Page size="A4" style={ReactToPdfStyles.page}>
        <View style={ReactToPdfStyles.header}>
          <Text style={ReactToPdfStyles.headerTitle}>Due Diligence Questionnaire</Text>
          <Text>{ddqDescriptionText}</Text>
        </View>
        {_.flatten(formSectionsPerPage).map((Section, i) => (
          <View key={i} style={ReactToPdfStyles.section}>
            <Section mode="print" form={form} />
          </View>
        ))}
      </Page>
    </Document>
  );

  return <PDFDocument title={title} pdfDocument={pdfDocument} />;
}
